// ─── webauthnPrf: fingerprint / Face ID unlock, one passkey, two wallets ───
//
// WHAT THIS IS NOT: it is not a second place your keys live, and it does not
// send anything anywhere. The keystores are unchanged — still AES-GCM blobs in
// localStorage. All this module changes is WHERE THE UNLOCK KEY COMES FROM:
// today you type a password and PBKDF2 stretches it; with this, the platform
// authenticator (Touch ID, Face ID, Windows Hello, Android fingerprint) hands
// back a 32-byte secret and that becomes the key instead.
//
// ─── The mechanism: WebAuthn PRF ───
// The PRF extension turns a passkey into a keyed hash function: give it a
// SALT, it returns HMAC(authenticator-held key, salt) — deterministic for the
// same passkey + salt, and unobtainable without a live user-verification
// gesture. The authenticator's key never leaves the secure element, so the
// derived secret cannot be read off disk, phished, or replayed from another
// origin (the browser binds PRF to this origin).
//
// ─── One passkey, INDEPENDENT per-wallet salts ───
// Registering a separate passkey per wallet would mean two enrollment prompts
// and two things to lose. Instead: one passkey, evaluated at a DIFFERENT salt
// per wallet (PRF_SALT_SOLANA vs PRF_SALT_HYPERLIQUID). The outputs are
// unrelated — recovering the Solana unlock key tells you nothing about the
// Hyperliquid one — so the wallets stay cryptographically isolated while the
// user touches one finger once.
//
// ─── Password is never removed ───
// PRF is not universal: older authenticators, some browsers, and any device
// the user hasn't enrolled simply don't have it. The passkey is also
// DEVICE-BOUND — a new phone has no passkey and no way to derive the key. So
// biometrics is strictly an ADDITIONAL envelope over the same secret; the
// password blob stays exactly where it is, and every failure path here falls
// back to "type your password". Losing the passkey must never mean losing
// the wallet.

const CRED_KEY = 'solcheddar_passkey_id'

// Salt labels — CHANGING THESE ORPHANS EXISTING BIOMETRIC BLOBS (the derived
// secret changes, so the blob no longer decrypts). Version them instead.
export const PRF_SALT_SOLANA = 'cheddar:wallet:solana:v1'
export const PRF_SALT_HYPERLIQUID = 'cheddar:wallet:hyperliquid:v1'

const b64enc = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)))
const b64dec = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0))

async function saltFor(label) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(label))
  return new Uint8Array(digest)
}

/** Is a passkey already enrolled on THIS device/browser? */
export function hasPasskey() {
  try { return !!localStorage.getItem(CRED_KEY) } catch { return false }
}

/**
 * Could this device plausibly do biometric unlock? True means a platform
 * authenticator exists. It does NOT promise PRF — Windows Hello answers TRUE
 * here and still can't derive a key — and since attachment is now unset, a
 * machine that answers FALSE may still enroll fine via a phone or security
 * key. So this only decides whether to OFFER enrollment; enrollment itself is
 * the only real test.
 */
export async function isBiometricAvailable() {
  try {
    if (typeof window === 'undefined') return false
    if (!window.isSecureContext) return false          // PRF needs HTTPS
    if (!window.PublicKeyCredential) return false
    if (!PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) return false
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch { return false }
}

/**
 * Enroll a passkey for this origin. Idempotent: if one is already stored,
 * returns it rather than piling up credentials on the user's device.
 *
 * rp.id is deliberately omitted so it defaults to the current origin — a
 * hardcoded domain would break local dev and any preview deployment, and a
 * WRONG rp.id fails at the authenticator with an opaque error.
 */
export async function registerPasskey(userLabel = 'Cheddar Terminal') {
  const existing = hasPasskey()
  if (existing) return localStorage.getItem(CRED_KEY)

  const cred = await navigator.credentials.create({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rp: { name: 'Cheddar Terminal' },
      user: {
        id: crypto.getRandomValues(new Uint8Array(16)),
        name: userLabel,
        displayName: userLabel,
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },     // ES256
        { type: 'public-key', alg: -257 },   // RS256
      ],
      authenticatorSelection: {
        // authenticatorAttachment is DELIBERATELY UNSET.
        //
        // Pinning it to 'platform' sent Windows straight to Windows Hello,
        // and Hello's authenticator does not implement CTAP2 hmac-secret —
        // the primitive PRF is built on. So on Windows the enrollment always
        // reached "this device can't derive a key", with no way out: the
        // browser was never allowed to offer anything else. (Android and iOS
        // platform authenticators DO implement it, which is why the phone
        // worked and the desktop didn't.)
        //
        // Unset, the browser shows its full picker: Hello, a phone over
        // hybrid/QR, a password-manager passkey, or a FIDO2 security key —
        // and the last three support PRF. Windows users pick one of those;
        // Mac/Android users still get their platform authenticator as the
        // default entry, so nothing regresses for them.
        userVerification: 'required',          // the gesture IS the point
        // residentKey MUST be 'required', and this is the whole Windows fix.
        // webauthn.dll only forwards the hmac-secret request to the
        // authenticator when the credential is being created as DISCOVERABLE;
        // ask for 'preferred' and Windows silently drops the extension, so
        // creation succeeds and PRF comes back disabled. Android and iOS
        // don't have that constraint — which is precisely why the phone
        // enrolled fine while Windows Hello reported "can't derive a key".
        // We store the credential id ourselves and don't need discoverability,
        // so this costs nothing but a passkey entry in the OS list.
        residentKey: 'required',
        requireResidentKey: true,              // legacy alias, older stacks read this one
      },
      timeout: 60_000,
      attestation: 'none',                     // we verify nothing server-side
      extensions: { prf: {} },
    },
  })
  if (!cred) throw new Error('Passkey creation was cancelled')

  // Some authenticators report prf.enabled at creation, some only prove it on
  // the first get(). A definite `false` is worth catching HERE: it means the
  // chosen authenticator can't do PRF at all, and saying so while the user
  // still remembers which one they picked is far more useful than a failure
  // one step later.
  const ext = cred.getClientExtensionResults?.()
  if (ext?.prf && ext.prf.enabled === false) {
    throw new Error(
      "That security method can't derive an encryption key. On Windows this usually means an " +
      'older build (PRF landed in 25H2) — update Windows, or choose a phone or security key instead.'
    )
  }
  const id = b64enc(cred.rawId)
  localStorage.setItem(CRED_KEY, id)
  return id
}

/** Drop the local passkey reference. Does not delete it from the OS keychain. */
export function forgetPasskey() {
  try { localStorage.removeItem(CRED_KEY) } catch {}
}

/**
 * Prompt for the biometric gesture and return the PRF secret for `label`, as
 * a base64 string suitable for use anywhere a password string is expected.
 * Throws on cancel, on no-passkey, and on PRF-unsupported — every caller
 * treats all three the same way: fall back to the password field.
 */
export async function getPrfSecret(label) {
  const credId = hasPasskey() ? localStorage.getItem(CRED_KEY) : null
  if (!credId) throw new Error('No passkey on this device — use your password')

  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      allowCredentials: [{ type: 'public-key', id: b64dec(credId) }],
      userVerification: 'required',
      timeout: 60_000,
      extensions: { prf: { eval: { first: await saltFor(label) } } },
    },
  })
  if (!assertion) throw new Error('Unlock was cancelled')

  const first = assertion.getClientExtensionResults?.()?.prf?.results?.first
  if (!first) {
    throw new Error(
      "This passkey can't derive an encryption key. Use your password, then turn fingerprint " +
      'unlock off and on again to re-enroll — or choose a phone or security key.'
    )
  }
  return b64enc(first)
}

/**
 * Enroll (if needed) and return the secret in one gesture-chain. If the
 * passkey turns out not to support PRF, the just-created credential is
 * FORGOTTEN — otherwise a useless id would sit in storage making the UI
 * claim biometrics is set up when it can never unlock anything.
 */
export async function enrollAndGetSecret(label, userLabel) {
  const wasEnrolled = hasPasskey()
  await registerPasskey(userLabel)
  try {
    return await getPrfSecret(label)
  } catch (err) {
    if (!wasEnrolled) forgetPasskey()
    throw err
  }
}